import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Activity, AlertTriangle, ArrowLeftRight, BarChart3, Coins, FileText, Gauge, HardHat, Landmark, LineChart, Receipt, RefreshCw, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { getErrorMessage } from "@/lib/errors";

import BudgetVsActualChart from "@/components/project/BudgetVsActualChart";
import { Input } from "@/components/ui/input";
const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const inrShort = (v) => {
  const n = Number(v || 0);
  const a = Math.abs(n);
  if (a >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (a >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return inr(n);
};
const pct = (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(1)}%`);
const CATEGORY_LABEL = { SUBCONTRACT: "Subcontract", MATERIAL: "Material", PLANT: "Equipment", LABOUR: "Labour", OTHER: "Other" };

export default function ProjectFinance() {
  const { projectId } = useProjectCtx();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/projects/${projectId}/finance`);
      setData(res);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <p className="text-sm text-muted-foreground" data-testid="project-finance-loading">Loading project finance…</p>;
  if (!data) return null;

  const { project, revenue, cost, margin, cash, payables, monthly, budget_lines, warnings } = data;
  const ev = data.earned_value_analysis;
  const marginTone = (v) => (Number(v) < 0 ? "rose" : "emerald");

  return (
    <div className="space-y-6" data-testid="project-finance-page">
      <PageHeader
        title="Project Finance"
        description={`${project.code} · ${project.name} — revenue, cost, margin, cash and payables in one place. Posted documents only.`}
        action={<Button variant="outline" onClick={load} disabled={loading}><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>}
      />

      {warnings.length > 0 && (
        <div className="space-y-1.5 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-400" data-testid="project-finance-warnings">
          {warnings.map((w, i) => <p key={i} className="flex gap-2"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{w}</p>)}
        </div>
      )}

      {/* Headline: one lead figure, three supporting ones */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="project-finance-lead">
        <LeadTile icon={Receipt} color="rose" label="Billed to client" value={inrShort(revenue.billed_gross)}
          ring={Number(project.contract_value) ? (Number(revenue.billed_gross) / Number(project.contract_value)) * 100 : 0} ringTone="text-slate-700 dark:text-slate-200"
          sub={`${pct(revenue.certified_pct)} certified · ${revenue.bills_count} RA bill(s) · of ${inrShort(project.contract_value)} contract`} />
        <LeadTile icon={Gauge} color="blue" label="Work done" value={pct(revenue.progress_pct)} bar={revenue.progress_pct} sub={`${inrShort(revenue.earned_value)} earned`} />
        <LeadTile icon={HardHat} color="violet" label="Actual cost" value={inrShort(cost.actual)} sub={`+ ${inrShort(cost.committed)} committed`} />
        <LeadTile icon={TrendingUp} color="emerald" label="Gross margin" value={inrShort(margin.gross_margin)} valueTone={marginTone(margin.gross_margin)}
          ring={margin.gross_margin_pct} ringTone={Number(margin.gross_margin) < 0 ? "text-rose-500" : "text-emerald-500"} sub={`${pct(margin.gross_margin_pct)} of billed`} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Revenue (Client)" icon={FileText} color="rose">
          {Number(project.variations_total || 0) !== 0 ? (
            <>
              <Row label="Original contract" value={inr(project.original_contract_value)} muted />
              <Row label="Approved variations" value={`${Number(project.variations_total) > 0 ? "+" : ""}${inr(project.variations_total)}`} muted />
              <Row label="Revised contract value" value={inr(project.revised_contract_value)} strong />
            </>
          ) : <Row label="Contract value" value={inr(project.contract_value)} />}
          <Link to={`/projects/${projectId}/variations`} className="block pt-1 text-xs font-medium text-[#910029] hover:underline dark:text-rose-300" data-testid="finance-variations-link">Variations →</Link>
          <Row label={`Work done (earned) · ${pct(revenue.progress_pct)}`} value={inr(revenue.earned_value)} />
          <Row label="Billed (gross, before GST)" value={inr(revenue.billed_gross)} strong />
          <Row label="Work done but not yet billed" value={inr(revenue.unbilled_work)} tone={Number(revenue.unbilled_work) > 0 ? "amber" : undefined} />
          <Row label="GST billed" value={inr(revenue.billed_gst)} muted />
          <Row label="Received from client" value={inr(revenue.received)} />
          <Row label="Receivable outstanding" value={inr(revenue.receivable_outstanding)} strong tone={Number(revenue.receivable_outstanding) > 0 ? "amber" : undefined} />
          <Row label="Retention held by client" value={inr(revenue.retention_held_by_client)} muted />
          <Row label="TDS deducted by client" value={inr(revenue.tds_deducted_by_client)} muted />
          {revenue.draft_bills > 0 && <p className="pt-2 text-xs text-muted-foreground">{revenue.draft_bills} draft RA bill(s) not yet posted — not included above.</p>}
        </Card>

        <Card title="Cost" icon={Coins} color="violet">
          {Object.keys(cost.by_category).length === 0 && <p className="py-1 text-sm text-muted-foreground">No cost booked yet.</p>}
          {Object.entries(cost.by_category).map(([k, v]) => <Row key={k} label={CATEGORY_LABEL[k] || k} value={inr(v)} />)}
          <Row label="Actual cost" value={inr(cost.actual)} strong />
          <Row label="Committed · subcontract (open WOs)" value={inr(cost.committed_subcontract)} muted />
          <Row label="Committed · material (open POs)" value={inr(cost.committed_material)} muted />
          <Row label="Actual + committed" value={inr(Number(cost.actual) + Number(cost.committed))} />
          <Row label="Approved budget" value={Number(cost.budget) ? inr(cost.budget) : "No approved budget"} />
          {Number(cost.variations_cost || 0) !== 0 && <Row label="… of which approved variations (baseline unchanged)" value={`${inr(cost.baseline_budget)} + ${inr(cost.variations_cost)}`} muted />}
          {Number(cost.budget) > 0 && <Row label="Budget used (actual + committed)" value={pct(cost.budget_used_pct)} tone={cost.budget_used_pct > 100 ? "rose" : undefined} />}
        </Card>

        <Card title="Margin & Forecast" icon={LineChart} color="emerald">
          <Row label="Gross margin (billed − actual cost)" value={inr(margin.gross_margin)} strong tone={marginTone(margin.gross_margin)} />
          <Row label="Gross margin %" value={pct(margin.gross_margin_pct)} tone={marginTone(margin.gross_margin)} />
          <Row label="Forecast final cost" value={inr(cost.forecast_final_cost)} />
          <Row label="Forecast margin at completion" value={inr(margin.forecast_margin)} strong tone={marginTone(margin.forecast_margin)} />
          <Row label="Forecast margin %" value={pct(margin.forecast_margin_pct)} tone={marginTone(margin.forecast_margin)} />
          <p className="pt-2 text-xs leading-relaxed text-muted-foreground" data-testid="forecast-explanation">
            {String(cost.forecast_basis || "").startsWith("budget")
              ? "Forecast final cost is what has already been spent plus what is still to come, worked out from your approved budget and the cost-to-complete estimate. It is never lower than actual cost plus committed orders."
              : "There is no approved budget yet, so the forecast is simply actual cost plus committed orders (ordered but not yet billed). Approve an estimate on Estimates & Budgets for a fuller forecast."}
          </p>
        </Card>

        <Card title="Cash & Payables" icon={Landmark} color="amber">
          <Row label="Cash in (client payments)" value={inr(cash.cash_in)} />
          <Row label="Cash out (paid to vendors & subcontractors)" value={inr(cash.cash_out)} />
          <Row label="Net cash position" value={inr(cash.net_cash)} strong tone={marginTone(cash.net_cash)} />
          {Number(cash.asset_purchases_paid) > 0 && (
            <Row label="Paid for company assets bought for this site (not project cost)" value={inr(cash.asset_purchases_paid)} muted />
          )}
          <div className="my-2 border-t border-border" />
          <Row label="Owed to subcontractors" value={inr(payables.subcontractors_outstanding)} />
          <Row label="Owed to suppliers" value={inr(payables.suppliers_outstanding)} />
          {Number(payables.assets_outstanding) > 0 && <Row label="Owed for company assets (not project cost)" value={inr(payables.assets_outstanding)} muted />}
          <Row label="Total payables" value={inr(payables.total_outstanding)} strong tone={Number(payables.total_outstanding) > 0 ? "amber" : undefined} />
          <Row label="Retention held from subcontractors" value={inr(payables.retention_held_from_subcontractors)} muted />
          <Row label="TDS deducted from subcontractors" value={inr(payables.tds_deducted_from_subcontractors)} muted />
        </Card>
      </div>

      <SurplusMemo memo={data.surplus} />

      {ev && (
        <div className="rounded-2xl border border-border/70 bg-card shadow-sm p-5" data-testid="project-finance-earned-value">
          <SectionTitle icon={Activity} color="blue">Earned value &amp; forecast at completion</SectionTitle>
          {!ev.available ? (
            <p className="py-1 text-sm text-muted-foreground">{ev.reason}</p>
          ) : (
            <>
              <div className="mt-2 grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Tile label="Budget (BAC)" value={inrShort(ev.bac)} sub="approved estimate" />
                <Tile label="Earned (EV)" value={inrShort(ev.ev)} sub={`${pct(ev.actual_pct)} of budget done`} />
                <Tile label="Forecast at completion" value={inrShort(ev.eac)} sub={`${Number(ev.vac) >= 0 ? "under" : "over"} budget by ${inrShort(Math.abs(Number(ev.vac)))}`} tone={Number(ev.vac) < 0 ? "rose" : "emerald"} />
                <Tile label="Still to spend" value={inrShort(ev.etc)} sub="forecast − actual so far" />
              </div>
              <div className="mt-3 grid gap-4 lg:grid-cols-2">
                <div className="divide-y divide-border/60">
                  <Row label={`Planned to be done by now · ${pct(ev.planned_pct)}`} value={inr(ev.pv)} />
                  <Row label={`Actually done · ${pct(ev.actual_pct)}`} value={inr(ev.ev)} />
                  <Row label="Spent so far" value={inr(ev.ac)} />
                  <Row label="Cost variance (earned − spent)" value={inr(ev.cv)} strong tone={Number(ev.cv) < 0 ? "rose" : "emerald"} />
                  <Row label="Schedule variance (earned − planned)" value={inr(ev.sv)} strong tone={Number(ev.sv) < 0 ? "rose" : "emerald"} />
                </div>
                <div className="divide-y divide-border/60">
                  <Row label="Cost performance (CPI) · 1.0 = on budget" value={ev.cpi === null ? "—" : ev.cpi.toFixed(2)} tone={ev.cpi !== null && ev.cpi < 1 ? "rose" : undefined} />
                  <Row label="Schedule performance (SPI) · 1.0 = on plan" value={ev.spi === null ? "—" : ev.spi.toFixed(2)} tone={ev.spi !== null && ev.spi < 1 ? "rose" : undefined} />
                  <Row label="Forecast if rest costs as budgeted" value={inr(ev.eac_budget_rate)} muted />
                  <Row label="Forecast if current overspend continues" value={ev.eac_cpi === null ? "—" : inr(ev.eac_cpi)} muted />
                  {ev.forecast_margin !== null && <Row label="Forecast margin on contract" value={inr(ev.forecast_margin)} strong tone={Number(ev.forecast_margin) < 0 ? "rose" : "emerald"} />}
                </div>
              </div>
              <p className="pt-2 text-xs text-muted-foreground">{ev.basis} Progress comes from Construction site progress, weighted as set in Item weights.</p>
            </>
          )}
        </div>
      )}

      <BudgetVsActualChart projectId={projectId} summary={cost} />

      {monthly.length > 0 && (
        <div className="rounded-2xl border border-border/70 bg-card shadow-sm p-5" data-testid="project-finance-monthly">
          <div className="mb-3"><SectionTitle icon={BarChart3} color="amber">Month by month (last 12 months)</SectionTitle></div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthly.map((m) => ({ ...m, billed: Number(m.billed), cost: Number(m.cost), cash_in: Number(m.cash_in), cash_out: Number(m.cash_out) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                <XAxis dataKey="month" stroke="#64748b" fontSize={11} />
                <YAxis stroke="#64748b" fontSize={11} tickFormatter={inrShort} width={80} />
                <Tooltip formatter={(v) => inr(v)} />
                <Legend />
                <Bar dataKey="billed" name="Billed" fill="#E3A817" radius={[3, 3, 0, 0]} />
                <Bar dataKey="cost" name="Cost" fill="#EF4444" radius={[3, 3, 0, 0]} />
                <Bar dataKey="cash_in" name="Cash in" fill="#10B981" radius={[3, 3, 0, 0]} />
                <Bar dataKey="cash_out" name="Cash out" fill="#64748B" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {budget_lines.length > 0 && (
        <div className="rounded-2xl border border-border/70 bg-card shadow-sm" data-testid="project-finance-budget-lines">
          <div className="border-b border-border/60 p-5"><SectionTitle icon={AlertTriangle} color="rose">Budget lines needing attention (lowest remaining first)</SectionTitle></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-[11px] uppercase tracking-wider text-muted-foreground">
                <tr>{["WBS", "Budget", "Actual", "Committed", "Remaining"].map((h, i) => <th key={h} className={`px-4 py-2 font-medium ${i ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {budget_lines.map((r) => (
                  <tr key={r.wbs_item_id} className="border-t border-border">
                    <td className="px-4 py-2 text-foreground"><span className="font-mono text-xs text-muted-foreground">{r.code}</span> {r.name}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs">{inr(r.budget)}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs">{inr(r.actual)}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs">{inr(r.committed)}</td>
                    <td className={`px-4 py-2 text-right font-mono text-xs font-semibold ${r.over_budget ? "text-rose-500" : "text-emerald-600 dark:text-emerald-400"}`}>{inr(r.variance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <ProjectCashFlowDetail projectId={projectId} />
    </div>
  );
}

const TONE = { rose: "text-rose-500", emerald: "text-emerald-600 dark:text-emerald-400", amber: "text-amber-600 dark:text-amber-400" };

const CARD = "rounded-2xl border border-border/70 bg-card shadow-sm";
const BADGE = {
  primary: "bg-primary/10 text-primary",
  rose: "bg-rose-500/10 text-rose-500",
  blue: "bg-blue-500/10 text-blue-500",
  violet: "bg-violet-500/10 text-violet-500",
  emerald: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  amber: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
};

function IconBadge({ icon: Icon, color = "primary" }) {
  return (
    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${BADGE[color] || BADGE.primary}`}>
      <Icon className="h-4 w-4" />
    </span>
  );
}

function SectionTitle({ icon, color, children }) {
  return (
    <div className="flex items-center gap-3">
      {icon && <IconBadge icon={icon} color={color} />}
      <p className="text-base font-semibold text-foreground">{children}</p>
    </div>
  );
}

function Ring({ value, tone = "text-foreground" }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  const r = 22, c = 2 * Math.PI * r;
  return (
    <div className="relative h-14 w-14 shrink-0" role="img" aria-label={`${v.toFixed(0)} percent`}>
      <svg viewBox="0 0 56 56" className="h-14 w-14 -rotate-90">
        <circle cx="28" cy="28" r={r} fill="none" strokeWidth="5" className="stroke-muted" />
        <circle cx="28" cy="28" r={r} fill="none" strokeWidth="5" strokeLinecap="round" strokeDasharray={`${(v / 100) * c} ${c}`} className={`stroke-current ${tone}`} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[11px] font-semibold tabular-nums text-foreground">{v.toFixed(0)}%</span>
    </div>
  );
}

function LeadTile({ icon, color, label, value, valueTone, sub, ring, ringTone, bar }) {
  return (
    <div className={`${CARD} p-5`} data-testid={`project-finance-tile-${label.toLowerCase().replaceAll(" ", "-")}`}>
      <div className="flex items-center gap-3">
        <IconBadge icon={icon} color={color} />
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      </div>
      <div className="mt-3 flex items-center justify-between gap-3">
        <p className={`font-mono text-2xl font-bold tabular-nums ${TONE[valueTone] || "text-foreground"}`}>{value}</p>
        {ring !== undefined && <Ring value={ring} tone={ringTone} />}
      </div>
      {bar !== undefined && (
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-muted" role="img" aria-label={`${Number(bar).toFixed(0)} percent`}>
          <div className="h-full rounded-full bg-primary" style={{ width: `${Math.max(0, Math.min(100, Number(bar) || 0))}%` }} />
        </div>
      )}
      {sub && <p className={`text-xs text-muted-foreground ${bar !== undefined ? "mt-2" : "mt-1"}`}>{sub}</p>}
    </div>
  );
}

function Tile({ label, value, sub, tone }) {
  return (
    <div className="rounded-xl border border-border/60 bg-muted/30 p-4" data-testid={`project-finance-tile-${label.toLowerCase().replaceAll(" ", "-")}`}>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-2 font-mono text-xl font-bold tabular-nums ${TONE[tone] || "text-foreground"}`}>{value}</p>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

function Card({ title, icon, color, children }) {
  return (
    <div className={`${CARD} p-5`}>
      <div className="mb-3"><SectionTitle icon={icon} color={color}>{title}</SectionTitle></div>
      <div className="divide-y divide-border/50">{children}</div>
    </div>
  );
}

function Row({ label, value, strong, muted, tone }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2.5 text-sm">
      <span className={strong ? "font-semibold text-foreground" : muted ? "text-muted-foreground/80" : "text-muted-foreground"}>{label}</span>
      <span className={`font-mono tabular-nums ${strong ? "font-semibold" : ""} ${TONE[tone] || (muted ? "text-muted-foreground" : "text-foreground")}`}>{value}</span>
    </div>
  );
}

function SurplusMemo({ memo }) {
  if (!memo || !memo.count) return null;
  return (
    <div className={`${CARD} p-5`} data-testid="project-finance-surplus">
      <SectionTitle icon={ArrowLeftRight} color="violet">Surplus transfers between sites · memo</SectionTitle>
      <p className="mt-2 text-xs text-muted-foreground">Material moved between sites at the sending site's cost. Not counted in actual cost or budget used; the cost reaches an activity only when the material is issued to work.</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <Tile label="Received from other sites" value={inr(memo.in_value)} />
        <Tile label="Sent to other sites" value={inr(memo.out_value)} />
      </div>
      <div className="mt-4 overflow-x-auto rounded-xl border border-border/60">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr>{["Date", "GRN", "", "Material", "Qty", "Value", "Other site"].map((h, i) => <th key={i} className={`px-3 py-2.5 font-medium ${i === 4 || i === 5 ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-border/50">
            {memo.rows.map((r, i) => (
              <tr key={i}>
                <td className="whitespace-nowrap px-3 py-2.5">{r.date || "—"}</td>
                <td className="px-3 py-2.5 font-mono text-xs">{r.grn_number || "—"}</td>
                <td className="px-3 py-2.5"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${r.direction === "IN" ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-amber-500/10 text-amber-600 dark:text-amber-400"}`}>{r.direction === "IN" ? "Received" : "Sent"}</span></td>
                <td className="px-3 py-2.5">{r.item_code ? `${r.item_code} · ` : ""}{r.item_name}</td>
                <td className="px-3 py-2.5 text-right font-mono tabular-nums">{Number(r.quantity).toLocaleString("en-IN")}</td>
                <td className="px-3 py-2.5 text-right font-mono tabular-nums">{inr(r.value)}</td>
                <td className="px-3 py-2.5">{r.other_project || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProjectCashFlowDetail({ projectId }) {
  const [range, setRange] = useState({ date_from: "", date_to: "" });
  const [cf, setCf] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const money = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const params = () => ({ date_from: range.date_from || undefined, date_to: range.date_to || undefined });
  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/finance/cash-flow`, { params: params() });
      setCf(data);
    } catch {
      setCf(null);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (projectId) load(); }, [projectId]);
  const exportCsv = async () => {
    const res = await api.get(`/projects/${projectId}/finance/cash-flow/export`, { params: params(), responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(res.data);
    a.download = "project_cash_flow.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };
  if (!cf) return null;
  const rows = cf.rows.filter((r) => filter === "ALL" || r.category === filter);
  return (
    <div className="rounded-2xl border border-border/70 bg-card shadow-sm" data-testid="project-cash-flow-detail">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-border px-4 py-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Cash flow — detail</p>
          <p className="text-xs text-muted-foreground">Every rupee received or paid for this project, with its document.</p>
        </div>
        <div className="flex flex-wrap items-end gap-2 text-xs">
          <label className="flex flex-col">From<Input type="date" value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} className="mt-1 rounded-md border border-border bg-background px-2 py-1" /></label>
          <label className="flex flex-col">To<Input type="date" value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} className="mt-1 rounded-md border border-border bg-background px-2 py-1" /></label>
          <button type="button" onClick={load} className="rounded-md border border-border px-3 py-1.5 hover:bg-muted">Show</button>
          <button type="button" onClick={exportCsv} className="rounded-md border border-border px-3 py-1.5 hover:bg-muted">Excel (CSV)</button>
        </div>
      </div>
      <div className="grid gap-3 p-4 sm:grid-cols-3">
        <div><p className="text-xs text-muted-foreground">Money in</p><p className="font-mono text-lg font-bold text-emerald-600 dark:text-emerald-400">{money(cf.totals.in)}</p></div>
        <div><p className="text-xs text-muted-foreground">Money out</p><p className="font-mono text-lg font-bold text-rose-500">{money(cf.totals.out)}</p></div>
        <div><p className="text-xs text-muted-foreground">Net cash</p><p className={`font-mono text-lg font-bold ${Number(cf.totals.net) < 0 ? "text-rose-500" : "text-foreground"}`}>{money(cf.totals.net)}</p></div>
      </div>
      <div className="flex flex-wrap gap-2 px-4 pb-3">
        <button type="button" onClick={() => setFilter("ALL")} className={`rounded-full border px-2.5 py-1 text-xs ${filter === "ALL" ? "border-foreground" : "border-border text-muted-foreground"}`}>All</button>
        {cf.by_category.map((c) => (
          <button type="button" key={c.category} onClick={() => setFilter(c.category)}
            className={`rounded-full border px-2.5 py-1 text-xs ${filter === c.category ? "border-foreground" : "border-border text-muted-foreground"}`}>
            {c.label} · <span className={c.direction === "IN" ? "text-emerald-600" : c.direction === "OUT" ? "text-rose-500" : "text-muted-foreground"}>{money(c.amount)}</span>
          </button>
        ))}
      </div>
      {cf.monthly.length > 0 && (
        <div className="overflow-x-auto px-4 pb-3">
          <table className="w-full text-xs">
            <thead className="text-muted-foreground"><tr><th className="py-1 text-left font-medium">Month</th><th className="text-right font-medium">In</th><th className="text-right font-medium">Out</th><th className="text-right font-medium">Net</th></tr></thead>
            <tbody>{cf.monthly.map((m) => (
              <tr key={m.month} className="border-t border-border"><td className="py-1">{m.month}</td><td className="text-right font-mono">{money(m.in)}</td><td className="text-right font-mono">{money(m.out)}</td>
                <td className={`text-right font-mono ${Number(m.net) < 0 ? "text-rose-500" : ""}`}>{money(m.net)}</td></tr>
            ))}</tbody>
          </table>
        </div>
      )}
      <div className="max-h-[480px] overflow-auto border-t border-border">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-card text-xs text-muted-foreground"><tr>
            <th className="px-4 py-2 text-left font-medium">Date</th><th className="px-2 py-2 text-left font-medium">Category</th><th className="px-2 py-2 text-left font-medium">Party</th>
            <th className="px-2 py-2 text-left font-medium">Document</th><th className="px-2 py-2 text-right font-medium">In</th><th className="px-2 py-2 text-right font-medium">Out</th><th className="px-4 py-2 text-right font-medium">Balance</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-xs text-muted-foreground">No money has moved for this project in this period.</td></tr>}
            {rows.map((r, i) => (
              <tr key={i} className={`border-t border-border ${r.category === "ASSET" ? "text-muted-foreground" : ""}`}>
                <td className="whitespace-nowrap px-4 py-1.5 text-xs">{r.date}</td>
                <td className="px-2 py-1.5 text-xs">{r.category_label}</td>
                <td className="px-2 py-1.5 text-xs">{r.party || "—"}</td>
                <td className="px-2 py-1.5 font-mono text-[11px]">{r.document}{r.note && <span className="font-sans text-muted-foreground"> · {r.note}</span>}</td>
                <td className="px-2 py-1.5 text-right font-mono text-xs text-emerald-600 dark:text-emerald-400">{r.direction === "IN" ? money(r.amount) : ""}</td>
                <td className="px-2 py-1.5 text-right font-mono text-xs text-rose-500">{r.direction === "OUT" ? money(r.amount) : ""}</td>
                <td className="px-4 py-1.5 text-right font-mono text-xs">{r.category === "ASSET" ? "(not counted)" : money(r.balance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
